<template>
    <div class="verification-page">
        <!-- Header -->
        <div class="verification-header">
            <div class="container">
                <div class="header-content">
                    <h1 class="logo">ICDGP</h1>
                    <p class="tagline">Institute of Cybersecurity and Data Governance Professionals</p>
                </div>
            </div>
        </div>

        <!-- Main Content -->
        <div class="container">
            <div class="verification-container">
                <!-- Loading State -->
                <div v-if="isLoading" class="verification-card">
                    <div class="loading-spinner">
                        <div class="spinner"></div>
                    </div>
                    <h2 class="loading-title">Verifying Certificate...</h2>
                    <p class="loading-message">Please wait while we verify certificate {{ certificateNumber }}</p>
                </div>

                <!-- Verification Result -->
                <div v-else class="verification-card">
                    <!-- Icon -->
                    <div class="verification-icon" :class="{ 'verified': verified, 'unverified': !verified }">
                        <i v-if="verified" class="bi bi-check-circle-fill"></i>
                        <i v-else class="bi bi-x-circle-fill"></i>
                    </div>

                    <!-- Title -->
                    <h2 class="verification-title" :class="{ 'text-success': verified, 'text-danger': !verified }">
                        {{ verified ? 'Certificate Verified' : 'Verification Failed' }}
                    </h2>

                    <!-- Message -->
                    <p class="verification-message">{{ message }}</p>

                    <!-- Certificate Details (if found and valid) -->
                    <div v-if="certificate" class="certificate-details">
                        <div class="detail-row">
                            <span class="detail-label">Certificate Number:</span>
                            <span class="detail-value">{{ certificate.certificate_number }}</span>
                        </div>

                        <div v-if="certificate.holder_name" class="detail-row">
                            <span class="detail-label">Certificate Holder:</span>
                            <span class="detail-value">{{ certificate.holder_name }}</span>
                        </div>

                        <div v-if="certificate.certificate_type" class="detail-row">
                            <span class="detail-label">Certificate Type:</span>
                            <span class="detail-value">{{ certificate.certificate_type }}</span>
                        </div>

                        <div v-if="certificate.program" class="detail-row">
                            <span class="detail-label">Program:</span>
                            <span class="detail-value">{{ certificate.program }}</span>
                        </div>

                        <div v-if="certificate.issue_date" class="detail-row">
                            <span class="detail-label">Issue Date:</span>
                            <span class="detail-value">{{ certificate.issue_date }}</span>
                        </div>

                        <div v-if="certificate.expiry_date" class="detail-row">
                            <span class="detail-label">Expiry Date:</span>
                            <span class="detail-value">{{ certificate.expiry_date }}</span>
                        </div>

                        <div class="detail-row">
                            <span class="detail-label">Status:</span>
                            <span class="detail-value" :class="{
                                'text-success': certificate.status === 'Active',
                                'text-danger': certificate.status === 'Expired' || certificate.status === 'Inactive'
                            }">
                                {{ certificate.status }}
                            </span>
                        </div>
                    </div>

                    <!-- Verification Badge -->
                    <div v-if="verified" class="verification-badge">
                        <i class="bi bi-patch-check-fill"></i>
                        <span>Officially Verified by ICDGP</span>
                    </div>

                    <!-- Actions -->
                    <div class="verification-actions">
                        <a href="/" class="btn btn-primary">
                            <i class="bi bi-house-door me-2"></i>
                            Go to Homepage
                        </a>
                        <button @click="verifyAnother" class="btn btn-outline-secondary">
                            <i class="bi bi-search me-2"></i>
                            Verify Another Certificate
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Footer -->
        <div class="verification-footer">
            <div class="container">
                <p class="footer-text">
                    &copy; {{ currentYear }} Institute of Cybersecurity and Data Governance Professionals. All rights reserved.
                </p>
                <p class="footer-info">
                    <i class="bi bi-info-circle me-1"></i>
                    For verification assistance, contact us at verify@icdgp.org
                </p>
            </div>
        </div>

        <!-- Verify Another Modal -->
        <div v-if="showVerifyModal" class="modal-overlay" @click="closeModal">
            <div class="modal-content" @click.stop>
                <div class="modal-header">
                    <h3>Verify Certificate</h3>
                    <button type="button" class="btn-close" @click="closeModal">
                        <i class="bi bi-x-lg"></i>
                    </button>
                </div>
                <div class="modal-body">
                    <label for="certificateNumber" class="form-label">Enter Certificate Number</label>
                    <input type="text" id="certificateNumber" v-model="searchCertificateNumber" class="form-control" placeholder="e.g., ICDGP-2025-8-819355" @keyup.enter="searchCertificate">
                </div>
                <div class="modal-footer">
                    <button @click="closeModal" class="btn btn-secondary">Cancel</button>
                    <button @click="searchCertificate" class="btn btn-primary">
                        <i class="bi bi-search me-2"></i>Verify
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { router } from '@inertiajs/vue3';

const props = defineProps({
    verified: {
        type: Boolean,
        required: true
    },
    message: {
        type: String,
        required: true
    },
    certificate: {
        type: Object,
        default: null
    },
    certificateNumber: {
        type: String,
        required: true
    }
});

const showVerifyModal = ref(false);
const searchCertificateNumber = ref('');
const isLoading = ref(true);
const currentYear = computed(() => new Date().getFullYear());

// Simulate loading state for better UX
onMounted(() => {
    setTimeout(() => {
        isLoading.value = false;
    }, 500);
});

const verifyAnother = () => {
    showVerifyModal.value = true;
    searchCertificateNumber.value = '';
};

const closeModal = () => {
    showVerifyModal.value = false;
};

const searchCertificate = () => {
    if (searchCertificateNumber.value.trim()) {
        isLoading.value = true;
        router.visit(`/verify/${searchCertificateNumber.value.trim()}`);
        closeModal();
    }
};
</script>

<style scoped>
/* General Styles */
.verification-page {
    min-height: 100vh;
    background: #ffffff;
    display: flex;
    flex-direction: column;
}

.container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1rem;
}

/* Header */
.verification-header {
    background: linear-gradient(135deg, #1e9579 0%, #2dd4bf 100%);
    padding: 2rem 0;
    margin-bottom: 3rem;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.header-content {
    text-align: center;
    color: white;
}

.logo {
    font-size: 3rem;
    font-weight: 800;
    margin: 0 0 0.5rem 0;
    color: white;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.tagline {
    font-size: 1.1rem;
    margin: 0;
    opacity: 0.9;
    font-weight: 300;
}

/* Main Container */
.verification-container {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem 0;
}

.verification-card {
    background: white;
    border-radius: 20px;
    padding: 3rem 2rem;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    border: 1px solid #e9ecef;
    max-width: 600px;
    width: 100%;
    text-align: center;
}

/* Loading State */
.loading-spinner {
    margin-bottom: 2rem;
}

.spinner {
    width: 60px;
    height: 60px;
    margin: 0 auto;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #1e9579;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.loading-title {
    font-size: 1.8rem;
    font-weight: 700;
    color: #2c3e50;
    margin-bottom: 1rem;
}

.loading-message {
    font-size: 1.1rem;
    color: #6c757d;
    line-height: 1.6;
}

/* Verification Icon */
.verification-icon {
    font-size: 5rem;
    margin-bottom: 1.5rem;
}

.verification-icon.verified {
    color: #28a745;
}

.verification-icon.unverified {
    color: #dc3545;
}

/* Title */
.verification-title {
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 1rem;
}

.text-success {
    color: #28a745;
}

.text-danger {
    color: #dc3545;
}

/* Message */
.verification-message {
    font-size: 1.1rem;
    color: #6c757d;
    margin-bottom: 2rem;
    line-height: 1.6;
}

/* Certificate Details */
.certificate-details {
    background: #f8f9fa;
    border-radius: 12px;
    padding: 2rem;
    margin-bottom: 2rem;
    text-align: left;
}

.detail-row {
    display: flex;
    justify-content: space-between;
    padding: 0.75rem 0;
    border-bottom: 1px solid #e9ecef;
}

.detail-row:last-child {
    border-bottom: none;
}

.detail-label {
    font-weight: 600;
    color: #495057;
}

.detail-value {
    color: #212529;
    font-weight: 500;
}

/* Verification Badge */
.verification-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
    color: white;
    padding: 0.75rem 1.5rem;
    border-radius: 25px;
    font-weight: 600;
    margin-bottom: 2rem;
    box-shadow: 0 4px 15px rgba(40, 167, 69, 0.3);
}

.verification-badge i {
    font-size: 1.5rem;
}

/* Actions */
.verification-actions {
    display: flex;
    gap: 1rem;
    justify-content: center;
    flex-wrap: wrap;
}

.btn {
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-weight: 600;
    border: none;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    transition: all 0.3s ease;
    text-decoration: none;
}

.btn-primary {
    background: linear-gradient(135deg, #1e9579 0%, #2dd4bf 100%);
    color: white;
}

.btn-primary:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(30, 149, 121, 0.4);
}

.btn-outline-secondary {
    background: transparent;
    border: 2px solid #6c757d;
    color: #6c757d;
}

.btn-outline-secondary:hover {
    background: #6c757d;
    color: white;
    transform: translateY(-2px);
}

/* Footer */
.verification-footer {
    background: #f8f9fa;
    padding: 2rem 0;
    margin-top: 3rem;
    border-top: 1px solid #e9ecef;
    color: #6c757d;
    text-align: center;
}

.footer-text {
    margin: 0 0 0.5rem 0;
    font-size: 0.9rem;
    opacity: 0.9;
}

.footer-info {
    margin: 0;
    font-size: 0.9rem;
    opacity: 0.8;
}

/* Modal Styles */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 1rem;
}

.modal-content {
    background: white;
    border-radius: 16px;
    max-width: 500px;
    width: 100%;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-header {
    padding: 1.5rem 2rem;
    border-bottom: 1px solid #e9ecef;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.modal-header h3 {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 700;
    color: #212529;
}

.btn-close {
    background: none;
    border: none;
    font-size: 1.5rem;
    color: #6c757d;
    cursor: pointer;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: all 0.3s ease;
}

.btn-close:hover {
    background: #f8f9fa;
    color: #495057;
}

.modal-body {
    padding: 2rem;
}

.form-label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 600;
    color: #495057;
}

.form-control {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 2px solid #e9ecef;
    border-radius: 8px;
    font-size: 1rem;
    transition: all 0.3s ease;
}

.form-control:focus {
    outline: none;
    border-color: #1e9579;
    box-shadow: 0 0 0 3px rgba(30, 149, 121, 0.1);
}

.modal-footer {
    padding: 1.5rem 2rem;
    border-top: 1px solid #e9ecef;
    display: flex;
    gap: 1rem;
    justify-content: flex-end;
}

.btn-secondary {
    background: #6c757d;
    color: white;
}

.btn-secondary:hover {
    background: #5a6268;
}

/* Responsive Design */
@media (max-width: 768px) {
    .logo {
        font-size: 2rem;
    }

    .tagline {
        font-size: 0.9rem;
    }

    .verification-card {
        padding: 2rem 1.5rem;
    }

    .verification-title {
        font-size: 1.5rem;
    }

    .verification-icon {
        font-size: 4rem;
    }

    .verification-actions {
        flex-direction: column;
    }

    .btn {
        width: 100%;
    }

    .certificate-details {
        padding: 1.5rem;
    }

    .detail-row {
        flex-direction: column;
        gap: 0.25rem;
    }
}

@media (max-width: 576px) {
    .verification-header {
        padding: 1.5rem 0;
    }

    .verification-card {
        padding: 1.5rem 1rem;
    }

    .verification-message {
        font-size: 1rem;
    }

    .modal-content {
        margin: 0 1rem;
    }
}
</style>
